import React, { useState, useEffect, useRef } from 'react';
import { User, Calendar, Building2, Hash, Droplet, Phone, Camera, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { handlePrintToPDF } from '../utils/prints';

export default function IDCardForm({eid, data}) {
  const printRef = useRef();
  const navigate = useNavigate();
  const { user, checkAuthStatus } = useAuth();
  
  const [formData, setFormData] = useState({
    name: '',
    dateOfJoining: '',
    department: '',
    employeeCode: '',
    bloodGroup: '',
    emergencyContactNumber: '',
    photograph: null
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errors, setErrors] = useState({});
  const [photographPreview, setPhotographPreview] = useState(null);

  const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const API_BASE_URL = `${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/emp-documnets/docs`;
  const handleFileUpload = async (docType, event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      // Create filename with eid prefix
      const serverFilename = `${!eid ? user.eid : eid}_${docType}`;
      
      // Create FormData for file upload
      const formData = new FormData();
      
      // Rename the file before uploading
      const renamedFile = new File([file], serverFilename, { type: file.type });
      formData.append('file', renamedFile);

      // Upload to API
      const response = await fetch(`${API_BASE_URL}?eid=${!eid ? user.eid : eid}`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      // Refresh the documents list to get the new URL
      // await fetchUploadedDocuments();

    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      event.target.value = ''; // Reset file input
    }
  };
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (5MB = 5 * 1024 * 1024 bytes)
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({
        ...prev,
        photograph: 'File size must be less than 5MB. Please upload a smaller file.'
      }));
      e.target.value = '';
      return;
    }
    
    // Check file type
    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({
        ...prev,
        photograph: 'Please upload an image file (PNG, JPG, etc.)'
      }));
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotographPreview(reader.result);
      setFormData(prev => ({
        ...prev,
        photograph: reader.result
      }));
    };
    reader.readAsDataURL(file);
    
    if (errors.photograph) {
      setErrors(prev => ({
        ...prev,
        photograph: ''
      }));
    }
    await handleFileUpload('photograph', e);
  };

  const removePhotograph = () => {
    setPhotographPreview(null);
    setFormData(prev => ({
      ...prev,
      photograph: null
    }));
    // Reset file input
    const fileInput = document.getElementById('photograph');
    if (fileInput) fileInput.value = '';
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.dateOfJoining) newErrors.dateOfJoining = 'Date of joining is required';
    if (!formData.department.trim()) newErrors.department = 'Department is required';
    // if (!formData.employeeCode.trim()) newErrors.employeeCode = 'Employee code is required';
    if (!formData.bloodGroup) newErrors.bloodGroup = 'Blood group is required';
    if (!formData.emergencyContactNumber.trim()) {
      newErrors.emergencyContactNumber = 'Emergency contact number is required';
    } else if (!/^[0-9]{10}$/.test(formData.emergencyContactNumber.replace(/\s+/g, ''))) {
      newErrors.emergencyContactNumber = 'Please enter a valid 10-digit phone number';
    }
    if (!formData.photograph) newErrors.photograph = 'Passport size photograph is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/employees/save-application`, {
        method: 'POST',
        body: JSON.stringify({
          eid: !eid ? user.eid : eid,
          applicationId: `KCL${new Date().getFullYear()}${String(Math.floor(Math.random() * 10000)).padStart(4, '0')}`,
          formType: "ID Card",
          submissionDate: new Date().toISOString(),
          data: formData,
          submitType: "Final"
        }),
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          dateOfJoining: '',
          department: '',
          employeeCode: '',
          bloodGroup: '',
          emergencyContactNumber: '',
          photograph: null
        });
        setPhotographPreview(null);
        checkAuthStatus();
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getEmployeeData = async (eid, formType) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/employees/get-application?eid=${eid}&formType=${formType}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  function extractEmployeeCore(finalData) {
    if (!finalData) return null;
    let data = finalData.data;

    if (!data) return null;
    return {
      name: `${data.formData.firstName ?? ""}${data.formData.middleName?" "+data.formData.middleName:""} ${data.formData.surname ?? ""}` || "",
      employeeCode: finalData.eid || "",
      bloodGroup: data.formData.bloodGroup || "",
      emergencyContactNumber: data.formData.emergencyPhone || "",
    };
  }

  const [isSubmitted, setIsSubmitted] = useState(false);

  const fetchEmployeeData = async () => {
    try {
      // First try ID Card API
      const idCardData = await getEmployeeData(user.eid, "ID Card");
  
      if (idCardData?.data) {
        setFormData(idCardData.data);
        if (idCardData.data.photograph) {
          setPhotographPreview(idCardData.data.photograph);
        }
        setIsSubmitted(idCardData.approved);
      } else {
        // If ID Card data is empty, try Personal API
        const personalData = await getEmployeeData(user.eid, "Personal");
        setFormData(prev => ({
          ...prev,
          ...extractEmployeeCore(personalData),
        }));
      }
  
    } catch (error) {
      // If ID Card API failed, fallback to Personal
      try {
        const personalData = await getEmployeeData(user.eid, "Personal");
        setFormData(prev => ({
          ...prev,
          ...extractEmployeeCore(personalData),
        }));
      } catch (err2) {
        console.error("Both API calls failed:", err2);
      }
    }
  };

  useEffect(() => {
    if (!user) return;

    if(data){
      setFormData(data);
      if (data.photograph) {
        setPhotographPreview(data.photograph);
      }
      return;
    }
    fetchEmployeeData();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-200 via-indigo-200 to-purple-200 py-12 px-4" ref={printRef}>
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="flex items-center space-x-3 justify-center">
            <img src="/images/kajaria-logo.png" alt="Kajaria" className="h-10" />
            <img src="/images/kerovit_logo.png" alt="Kerovit" className="h-10" />
            <img src="/images/gresbond-logo.png" alt="Gresbond" className="h-10" />
          </div>
          
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Employee ID Card Details</h1>
          <p className="text-gray-600">Please fill in all the required information to generate your ID card</p>
        </div>

        {submitStatus === 'success' && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <p className="text-green-700">ID card details submitted successfully!</p>
          </div>
        )}
        
        {submitStatus === 'error' && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-red-700">There was an error submitting your details. Please try again.</p>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <User className="w-4 h-4 inline mr-2" />
              Full Name *
            </label>
            <input
              type="text"
              disabled={isSubmitted}
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border-2 rounded-lg transition-colors focus:outline-none focus:ring-0 ${
                errors.name 
                  ? 'border-red-300 focus:border-red-500' 
                  : 'border-gray-200 focus:border-blue-500'
              }`}
              placeholder="Enter your full name"
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <Calendar className="w-4 h-4 inline mr-2" />
              Date of Joining *
            </label>
            <input
              type="date"
              disabled={isSubmitted}
              name="dateOfJoining"
              value={formData.dateOfJoining}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border-2 rounded-lg transition-colors focus:outline-none focus:ring-0 ${
                errors.dateOfJoining 
                  ? 'border-red-300 focus:border-red-500' 
                  : 'border-gray-200 focus:border-blue-500'
              }`}
            />
            {errors.dateOfJoining && <p className="mt-1 text-sm text-red-600">{errors.dateOfJoining}</p>}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <Building2 className="w-4 h-4 inline mr-2" />
              Department *
            </label>
            <input
              type="text"
              disabled={isSubmitted}
              name="department"
              value={formData.department}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border-2 rounded-lg transition-colors focus:outline-none focus:ring-0 ${
                errors.department 
                  ? 'border-red-300 focus:border-red-500' 
                  : 'border-gray-200 focus:border-blue-500'
              }`}
              placeholder="e.g., Human Resources, IT, Finance"
            />
            {errors.department && <p className="mt-1 text-sm text-red-600">{errors.department}</p>}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <Hash className="w-4 h-4 inline mr-2" />
              Employee Code
            </label>
            <input
              type="text"
              disabled={isSubmitted}
              name="employeeCode"
              value={formData.employeeCode}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border-2 rounded-lg transition-colors focus:outline-none focus:ring-0 ${
                errors.employeeCode 
                  ? 'border-red-300 focus:border-red-500' 
                  : 'border-gray-200 focus:border-blue-500'
              }`}
              placeholder="Enter your employee code"
            />
            {errors.employeeCode && <p className="mt-1 text-sm text-red-600">{errors.employeeCode}</p>}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <Droplet className="w-4 h-4 inline mr-2" />
              Blood Group *
            </label>
            <select
              disabled={isSubmitted}
              name="bloodGroup"
              value={formData.bloodGroup}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border-2 rounded-lg transition-colors focus:outline-none focus:ring-0 ${
                errors.bloodGroup 
                  ? 'border-red-300 focus:border-red-500' 
                  : 'border-gray-200 focus:border-blue-500'
              }`}
            >
              <option value="">Select your blood group</option>
              {bloodGroups.map(group => (
                <option key={group} value={group}>{group}</option>
              ))}
            </select>
            {errors.bloodGroup && <p className="mt-1 text-sm text-red-600">{errors.bloodGroup}</p>}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <Phone className="w-4 h-4 inline mr-2" />
              Emergency Contact Number *
            </label>
            <input
              type="tel"
              disabled={isSubmitted}
              name="emergencyContactNumber"
              value={formData.emergencyContactNumber}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border-2 rounded-lg transition-colors focus:outline-none focus:ring-0 ${
                errors.emergencyContactNumber 
                  ? 'border-red-300 focus:border-red-500' 
                  : 'border-gray-200 focus:border-blue-500'
              }`}
              placeholder="Enter 10-digit phone number"
            />
            {errors.emergencyContactNumber && <p className="mt-1 text-sm text-red-600">{errors.emergencyContactNumber}</p>}
          </div>

          <div className="pdf-break">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <Camera className="w-4 h-4 inline mr-2" />
              Passport Size Photograph *
            </label>
            <p className="text-xs text-gray-600 mb-2">Upload photograph (Max size: 5MB, Formats: PNG, JPG, JPEG)</p>
            
            {!photographPreview ? (
              <div className={`border-2 border-dashed rounded-lg p-6 transition-colors ${
                errors.photograph 
                  ? 'border-red-300 bg-red-50' 
                  : 'border-gray-300 hover:border-blue-400 bg-gray-50'
              }`}>
                <input
                  type="file"
                  disabled={isSubmitted}
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                  id="photograph"
                />
                <label htmlFor="photograph" className="cursor-pointer block text-center">
                  <Camera className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 mb-1">
                    Click to upload photograph
                  </p>
                  <p className="text-xs text-gray-500">PNG, JPG up to 5MB</p>
                </label>
              </div>
            ) : (
              <div className="border-2 border-gray-300 rounded-lg p-4 bg-white">
                <div className="flex justify-between items-start mb-2">
                  <p className="text-sm font-medium text-gray-700">Preview:</p>
                  <button
                    type="button"
                    disabled={isSubmitted}
                    onClick={removePhotograph}
                    className="text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    Remove
                  </button>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg flex justify-center items-center">
                  <img
                    src={photographPreview}
                    alt="Passport Photograph"
                    className="max-h-64 max-w-full object-contain rounded-lg shadow-sm"
                  />
                </div>
              </div>
            )}
            {errors.photograph && <p className="mt-1 text-sm text-red-600">{errors.photograph}</p>}
          </div>

          <div className="no-print flex flex-col sm:flex-row gap-4 pt-6">
            <button
              type="button"
              onClick={()=>handlePrintToPDF(printRef, formData.name)}
              className="flex-1 bg-gray-500 text-white py-3 px-6 rounded-md hover:bg-gray-600 transition duration-200 font-medium"
            >
              Print
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting||isSubmitted}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 px-6 rounded-lg font-semibold text-lg shadow-lg hover:from-blue-700 hover:to-indigo-700 transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
              >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Submit ID Card Details
                </>
              )}
            </button>
          </div>
        </div>

        <div className="text-center mt-8 text-gray-500 text-sm">
          <p>All fields marked with * are required</p>
        </div>
      </div>
    </div>
  );
}